
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SecurityQuestionSelect } from "@/components/auth/SecurityQuestionSelect";
import { SecurityQuestion } from "@/components/auth/signup/types";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { useTranslation } from "@/hooks/useTranslation";

interface SecurityQuestionData {
  question1: SecurityQuestion;
  answer1: string;
  question2: SecurityQuestion;
  answer2: string;
}

interface SecurityQuestionsFormProps {
  isMobile: boolean;
}

type HashValueResponse = { data: string | null; error: Error | null };

export function SecurityQuestionsForm({ isMobile }: SecurityQuestionsFormProps) {
  const { t } = useTranslation();
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isSavingQuestions, setIsSavingQuestions] = useState(false);
  const [hasSecurityQuestions, setHasSecurityQuestions] = useState<boolean | null>(null);
  const [securityData, setSecurityData] = useState<SecurityQuestionData>({
    question1: "What brand was your first camera?",
    answer1: "",
    question2: "What was your first pet's name?",
    answer2: ""
  });

  useEffect(() => {
    fetchSecurityQuestions();
  }, []);

  const fetchSecurityQuestions = async () => {
    try {
      setIsLoadingQuestions(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('security_answers')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSecurityData({
          question1: data.question as SecurityQuestion,
          answer1: "",
          question2: data.question_two as SecurityQuestion,
          answer2: ""
        });
        setHasSecurityQuestions(true);
      } else {
        setHasSecurityQuestions(false);
      }
    } catch (error: any) {
      console.error('Error fetching security questions:', error);
      toast.error("Failed to fetch security questions");
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleSecurityQuestionsUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!securityData.answer1 || !securityData.answer2) {
      toast.error("Please provide answers for both security questions");
      return;
    }

    if (securityData.question1 === securityData.question2) {
      toast.error("Please select different security questions");
      return;
    }

    setIsSavingQuestions(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: hashedAnswer1, error: error1 } = await supabase.rpc('hash_value', {
        input: securityData.answer1.toLowerCase().trim()
      }) as HashValueResponse;

      const { data: hashedAnswer2, error: error2 } = await supabase.rpc('hash_value', {
        input: securityData.answer2.toLowerCase().trim()
      }) as HashValueResponse;

      if (error1 || error2 || !hashedAnswer1 || !hashedAnswer2) {
        throw new Error('Failed to process security answers');
      }

      const securityRecord: Database['public']['Tables']['security_answers']['Insert'] = {
        user_id: user.id,
        email: user.email || '',
        question: securityData.question1,
        answer: hashedAnswer1,
        question_two: securityData.question2,
        answer_two: hashedAnswer2
      };

      if (hasSecurityQuestions) {
        const { error } = await supabase
          .from('security_answers')
          .update(securityRecord)
          .eq('user_id', user.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('security_answers')
          .insert([securityRecord]);
        
        if (error) throw error;
      }

      toast.success("Security questions updated successfully");
      setHasSecurityQuestions(true);
      
      // Clear answers after successful update
      setSecurityData(prev => ({
        ...prev,
        answer1: "",
        answer2: ""
      }));
    } catch (error: any) {
      toast.error("Failed to update security questions");
    } finally {
      setIsSavingQuestions(false);
    }
  };

  if (isLoadingQuestions) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="text-left">
        <CardTitle>
          {hasSecurityQuestions ? t('update_security_questions') : t('setup_security_questions')}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-left">
        <form onSubmit={handleSecurityQuestionsUpdate} className="space-y-4">
          <SecurityQuestionSelect
            id="security-question-1"
            value={securityData.question1}
            onChange={(q) => setSecurityData({ ...securityData, question1: q as SecurityQuestion })}
            label={`${t('security_question')} 1`}
            excludeQuestion={securityData.question2}
          />
          <div className="space-y-2">
            <Label htmlFor="security-answer-1">{t('your_answer')} 1</Label>
            <Input
              id="security-answer-1"
              type="text"
              value={securityData.answer1}
              onChange={(e) => setSecurityData({ ...securityData, answer1: e.target.value })}
              required
              placeholder={t('enter_your_answer')}
            />
          </div>

          <SecurityQuestionSelect
            id="security-question-2"
            value={securityData.question2}
            onChange={(q) => setSecurityData({ ...securityData, question2: q as SecurityQuestion })}
            label={`${t('security_question')} 2`}
            excludeQuestion={securityData.question1}
          />
          <div className="space-y-2">
            <Label htmlFor="security-answer-2">{t('your_answer')} 2</Label>
            <Input
              id="security-answer-2"
              type="text"
              value={securityData.answer2}
              onChange={(e) => setSecurityData({ ...securityData, answer2: e.target.value })}
              required
              placeholder={t('enter_your_answer')}
            />
          </div>

          <Button 
            type="submit" 
            disabled={isSavingQuestions}
            className={isMobile ? 'w-full' : ''}
          >
            {isSavingQuestions && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {hasSecurityQuestions ? t('update_security_questions') : t('setup_security_questions')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
